import { useQuery } from '@tanstack/react-query';
import { Dispatch } from 'redux';

import { requestHandler } from '../../api';
import {
  DocumentSummary,
  DocumentWithArrearCalculationResponse,
  EditDocumentsInput,
  EditDocumentsOutput,
  GetDocumentsResponse,
  UpdateArrearRateInput,
  UpdateArrearRateOutput,
} from './types';

export { DocumentOperationOrigin } from './types';
export type {
  ArrearStructure,
  UpdateArrearRateInput,
  UpdateArrearRateOutput
} from './types';

export async function updateArrearRate(
  dispatch: Dispatch,
  data: UpdateArrearRateInput,
): Promise<UpdateArrearRateOutput | undefined> {
  const response = await requestHandler({
    method: 'patch',
    url: 'internal/document/arrearRate',
    data,
    dispatch,
  });
  if (!response || !('data' in response)) return undefined;
  return response.data as UpdateArrearRateOutput;
}

export const editDocuments = async (
  dispatch: Dispatch,
  data: EditDocumentsInput,
): Promise<EditDocumentsOutput | undefined> => {
  const response = await requestHandler({
    method: 'patch',
    url: 'internal/document/editDocuments',
    data,
    dispatch,
  });
  if (response) return response.data;
};

export const getDocumentReport = (dispatch: Dispatch, documentId: string) =>
  useQuery({
    queryKey: ['document', documentId, 'report'],
    queryFn: async (): Promise<DocumentSummary | undefined> => {
      const response = await requestHandler({
        method: 'get',
        url: `internal/report/document/${documentId}`,
        dispatch,
      });
      if (response) return response.data;
    },
    retry: 1,
    refetchOnWindowFocus: false,
  });

export async function getCessionCertificate(
  dispatch: Dispatch,
  documentId: string,
  signal?: AbortSignal,
): Promise<Blob | undefined> {
  const response = await requestHandler({
    responseType: 'blob',
    method: 'get',
    url: `internal/collection/cessionsCertificate/${documentId}`,
    signal,
    dispatch,
  });
  if (response) return response.data;
}

export const getDocumentsWithArrearCalculation = (
  dispatch: Dispatch,
  documentIds: string[],
  dateInCalculation: Date,
) =>
  useQuery({
    queryKey: [
      'documents',
      'withArrearCalculation',
      ...documentIds,
      dateInCalculation,
    ],
    queryFn: async (): Promise<
      DocumentWithArrearCalculationResponse | undefined
    > => {
      const query = `documentIds=${documentIds.join(',')}&dateInCalculation=${dateInCalculation.toISOString()}`;
      const response = await requestHandler({
        method: 'get',
        url: `internal/document/withArrearCalculation?${query}`,
        dispatch,
      });
      if (response) return response.data;
    },
    enabled: documentIds.length > 0 && !!dateInCalculation?.getTime(),
    retry: 1,
  });

export const getDocuments = (dispatch: Dispatch, documentIds: string[]) =>
  useQuery({
    queryKey: ['documents', documentIds],
    queryFn: async (): Promise<GetDocumentsResponse | undefined> => {
      const query = `documentIds=${documentIds.join(',')}`;
      const response = await requestHandler({
        method: 'get',
        url: `internal/document?${query}`,
        dispatch,
      });
      if (response) return response.data;
    },
    enabled: documentIds.length > 0,
    retry: 1,
  });
